import React from "react";
import { Button } from "@/ds";
import { roleAtLeast } from "@/lib/roles";
import type { PushToast } from "@/notifications/toast-queue";
import { useT } from "../i18n";
import { useSession } from "../session";
import type { EmailQaSection } from "../router";
import { DeniedScreen } from "./screen-denied";
import { EmailQaScreen } from "./screen-email-qa";
import { EmailQaNav } from "./email-qa-nav";
import { TestMailPanel } from "./test-mail-panel";
import { testMailContextKey } from "./test-mail-access";
import styles from "./screen-email-qa.module.css";

/** Branch before mounting either workflow: an admin visit never starts owner Lab effects. */
export function EmailQaEntry({ section: routedSection, runId, onSectionChange, onRunChange,
  onOpenRequest, onHome, pushToast }: {
  section?: EmailQaSection;
  runId?: string | null;
  onSectionChange: (section: EmailQaSection) => void;
  onRunChange: (runId: string | null) => void;
  onOpenRequest: (requestId: string) => void;
  onHome: () => void;
  pushToast: PushToast;
}) {
  const t = useT();
  const session = useSession();
  const owner = session.user?.role === "owner";
  const admin = !!session.user && roleAtLeast(session.user.role, "admin");
  const enabled = testMailContextKey(session) !== null;
  const section = routedSection ?? (owner ? "runs" : "test-mail");

  React.useEffect(() => {
    if (admin && !owner && enabled && !routedSection && !runId) onSectionChange("test-mail");
  }, [admin, owner, enabled, routedSection, runId, onSectionChange]);

  if (!admin) return <DeniedScreen reason="admin" area={t("emailQa.title")} onHome={onHome} />;
  if (section !== "test-mail" || runId) {
    return owner ? <EmailQaScreen section={section === "test-mail" ? "runs" : section}
      onSectionChange={onSectionChange} runId={runId} onRunChange={onRunChange}
      onOpenTestMail={enabled ? () => onSectionChange("test-mail") : undefined} />
      : <DeniedScreen reason="owner" area={t("emailQa.title")} onHome={onHome} />;
  }

  return <div className={`page page--management ${styles.page}`}>
    <div className="page-head"><div>
      <h1>{t(enabled ? "emailQa.title" : "testMail.unavailable")}</h1>
      <p>{t(enabled ? "testMail.sectionHint" : "testMail.unavailableCopy")}</p>
    </div></div>
    {(owner || enabled) && <EmailQaNav section="test-mail" owner={owner}
      testMailEnabled={enabled} onChange={onSectionChange} />}
    <div id="email-qa-panel" role={enabled ? "tabpanel" : "region"} aria-labelledby={enabled ? "email-qa-panel-tab-test-mail" : undefined}
      aria-label={enabled ? undefined : t("testMail.unavailable")} tabIndex={0}>
    {enabled ? <div className={styles.testMail}>
      <TestMailPanel pushToast={pushToast} onOpenRequest={onOpenRequest} />
    </div> : <Button variant="secondary" onClick={onHome}>{t("denied.toInbox")}</Button>}
    </div>
  </div>;
}
